import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
	ModelOfBidding,
	ModelOfContractBidding,
	ModelOfDemand,
	ModelOfImageFromDb,
	ModelOfOrderService,
	ModelOfUnityContract,
	ModelOfVehicleItem,
} from 'src/database';
import api from 'src/services/api';
import { ModelOfDataState } from './model';

const demandServiceEmptyObj = {
	ID: '',
	ORGAO: '',
	NOME_ORGAO: '',
	DESCRICAO: '',
	PESSOAORIGEM: '',
	NOME_PESSOAORIGEM: '',
	PESSOADESTINO: '',
	NOME_PESSOADESTINO: '',
	ORGAOUNIDADEDESTINO: '',
	NOME_ORGAOUNIDADEDESTINO: '',
	DATA: '',
	PRIORIDADE: '',
	DESCRICAO_PRIORIDADE: '',
	TIPODEMANDA: '',
	STATUS_ORDEMSERVICO: 0,
	FT_USUARIO: '',
	FT_VEICULO: '',
	DESCRICAO_VEICULO: '',
	PLACA_VEICULO: '',
	ANOMODELO: 0,
	NUMEROCHASSI: '',
	NUMEROMOTOR: '',
	MARCADORKM: 0,
	RENAVAM: '',
	COR: '',
	PRODUTOS: [],
	SERVICOS: [],
	QUANTIDADECOMBUSTIVELSOLICITADO: 0,
	QUANTIDADEEFETIVADA: 0,
};

const orderServiceEmptyObj = {
	ID: '',
	ORGAO: '',
	NOME_ORGAO: '',
	DATA_ORDEMSERVICO: '',
	DATAEFETIVADA: '',
	DATAALTERACAO: '',
	FT_USUARIO: '',
	STATUS: 0,
	VEICULOKM: '',
	DEMANDA: '',
	PRIORIDADE: '',
	DESCRICAO_PRIORIDADE: '',
	DATA_DEMANDA: '',
	DESCRICAO_DEMANDA: '',
	PESSOAORIGEM: '',
	NOME_PESSOAORIGEM: '',
	PESSOADESTINO: '',
	NOME_PESSOADESTINO: '',
	ORGAOUNIDADEDESTINO: '',
	NOME_ORGAOUNIDADEDESTINO: '',
	ID_VEICULO: '',
	DESCRICAO_VEICULO: '',
	PLACA_VEICULO: '',
	ANOMODELO: 0,
	NUMEROCHASSI: '',
	NUMEROMOTOR: '',
	MARCADORKM: 0,
	RENAVAM: '',
	COR: '',
	SERVICOS: [],
	PRODUTOS: [],
	TIPOORDEMSERVICO: '',
};

const DataContext = createContext<ModelOfDataState>({} as ModelOfDataState);

export const DataProvider: React.FC = ({ children }) => {
	const [vehicleDataSelected, setVehicleDataSelected] = useState<ModelOfVehicleItem | null>(null);

	const [currentBidding, setCurrentBidding] = useState<ModelOfBidding | null>(null);
	const [selectedBudgetUnit, setSelectedBudgetUnit] = useState<ModelOfUnityContract | null>(null);
	const [selectedContractBidding, setSelectedContractBidding] = useState<ModelOfContractBidding | null>(null);
	const [budgetUnits, setBudgetUnits] = useState<ModelOfUnityContract[]>([]);
	const [loadingUnits, setLoadingUnits] = useState(false);

	const [selectedDemand, setSelectedDemand] = useState<ModelOfDemand>(demandServiceEmptyObj);
	const [selectedOrderService, setSelectedOrderService] = useState<ModelOfOrderService>(orderServiceEmptyObj);

	const [imagesData, setImagesData] = useState<ModelOfImageFromDb[]>([]);
	const [imageUploading, setImageUploading] = useState<any[]>([]);
	const [loadingImage, setLoadingImage] = useState(false);

	const [fileImagesUpload, setFileImagesUpload] = useState<any[]>([]);
	const [imagePreview, setImagePreview] = useState<any[]>([]);
	const [disable, setDisable] = useState(false);

	const history = useHistory();

	function handleVehicleSelected(vehicle: ModelOfVehicleItem) {
		setVehicleDataSelected(vehicle);
		sessionStorage.setItem('@vehicleCurrent', JSON.stringify(vehicle));
		history.push('/vehicleMovimentations');
	}

	useMemo(() => {
		setLoadingUnits(true);
		getBudgetUnits(currentBidding);
	}, [budgetUnits.length, currentBidding?.ID]);

	function getBudgetUnits(bidding: ModelOfBidding | null) {
		if (!!bidding) {
			api.get(`budget-unit/${bidding.ID}`)
				.then((response) => {
					sessionStorage.setItem('@budgetUnits', JSON.stringify(response.data));
					setBudgetUnits(response.data);
					setLoadingUnits(false);
				})
				.catch((err) => {
					console.log(err);
					setLoadingUnits(false);
				});
		}
	}

	function handleBidding(bidding: ModelOfBidding) {
		setCurrentBidding(bidding);
		sessionStorage.setItem('@biddingCurrent', JSON.stringify(bidding));
		getBudgetUnits(bidding);
	}

	function handleBudgetUnit(unit: ModelOfUnityContract) {
		setSelectedBudgetUnit(unit);
		sessionStorage.setItem('@selectedBudgetUnit', JSON.stringify(unit));
		history.push('/contractForm');
	}

	function handleContractBidding(contract: ModelOfContractBidding) {
		setSelectedContractBidding(contract);
		sessionStorage.setItem('@selectedContractBidding', JSON.stringify(contract));
	}

	function handleSelectedDemand(demand: ModelOfDemand) {
		setSelectedDemand(demand);
		sessionStorage.setItem('@demandSelected', JSON.stringify(demand));
		history.push('/orderServiceForm');
	}

	function handleSelectedOrderService(OrderService: ModelOfOrderService) {
		setSelectedOrderService(OrderService);
		sessionStorage.setItem('@orderServiceSelected', JSON.stringify(OrderService));
		history.push('/orderServiceEditForm');
	}

	useEffect(() => {
		let vehicle = sessionStorage.getItem('@vehicleCurrent');
		setVehicleDataSelected(JSON.parse(vehicle || '{}'));
		let demand = sessionStorage.getItem('@demandSelected');
		setSelectedDemand(JSON.parse(demand || '{}'));
		let orderService = sessionStorage.getItem('@orderServiceSelected');
		setSelectedOrderService(JSON.parse(orderService || '{}'));
		let currentBidding = sessionStorage.getItem('@biddingCurrent');
		setCurrentBidding(JSON.parse(currentBidding || '{}'));
		let budgetUnits = sessionStorage.getItem('@budgetUnits');
		setBudgetUnits(JSON.parse(budgetUnits || '{}'));
		let selectedBudgetUnit = sessionStorage.getItem('@selectedBudgetUnit');
		setSelectedBudgetUnit(JSON.parse(selectedBudgetUnit || '{}'));
		let selectedContractBidding = sessionStorage.getItem('@selectedContractBidding');
		setSelectedContractBidding(JSON.parse(selectedContractBidding || '{}'));

		if (window.location.hash !== 'orderServiceForm') {
			sessionStorage.removeItem('@demandSelected');
			setSelectedDemand(demandServiceEmptyObj);
		}
	}, []);

	const handleRmFromServer = async (imageId: ModelOfImageFromDb | null) => {
		setLoadingImage(true);
		try {
			if (imageId) {
				await api.delete(`upload/${imageId.ID}`);
				getImages(imageId.TABLEAID);
			}
			setLoadingImage(false);
		} catch (err) {
			console.log(err);
			setLoadingImage(false);
		}
	};

	const handleDelete = (index?: number) => {
		if (index) {
			let copyImageUploading = Array.from(imageUploading);
			let copyImageFiles = Array.from(fileImagesUpload);
			let copyImagePreview = Array.from(imagePreview);
			copyImageUploading.splice(index, 1);
			copyImageFiles.splice(index, 1);
			copyImagePreview.splice(index, 1);
			setImageUploading(copyImageUploading);
			setFileImagesUpload(copyImageFiles);
			setImagePreview(copyImagePreview);
		} else {
			let copyImageUploading = Array.from(imageUploading);
			let copyImageFiles = Array.from(fileImagesUpload);
			let copyImagePreview = Array.from(imagePreview);
			copyImageUploading.splice(0, copyImageUploading.length);
			copyImageFiles.splice(0, copyImageFiles.length);
			copyImagePreview.splice(0, copyImagePreview.length);
			setImageUploading(copyImageUploading);
			setFileImagesUpload(copyImageFiles);
			setImagePreview(copyImagePreview);
		}

		if (imagePreview.length && fileImagesUpload.length) setDisable(false);
	};

	function handleImageUploading(file: any) {
		setImageUploading(imageUploading.concat(file));
	}

	const getImages = (itemId?: string) => {
		api.post('get-images', { TABLEAID: itemId })
			.then((response) => {
				setImagesData(response.data);
			})
			.catch((err) => {
				console.log(err);
			});
	};

	return (
		<DataContext.Provider
			value={{
				vehicleDataSelected,
				handleVehicleSelected,
				selectedContractBidding,
				setSelectedContractBidding,
				handleContractBidding,
				getBudgetUnits,
				selectedBudgetUnit,
				handleBudgetUnit,
				budgetUnits,
				loadingUnits,
				currentBidding,
				setCurrentBidding,
				setSelectedBudgetUnit,
				setBudgetUnits,
				handleBidding,
				selectedDemand,
				handleSelectedDemand,
				selectedOrderService,
				handleSelectedOrderService,
				imagePreview,
				fileImagesUpload,
				imageUploading,
				disable,
				setDisable,
				setFileImagesUpload,
				setImagePreview,
				setImageUploading,
				handleRmFromServer,
				handleDelete,
				handleImageUploading,
				loadingImage,
				imagesData,
				getImages,
			}}>
			{children}
		</DataContext.Provider>
	);
};

function useData() {
	const context = useContext(DataContext);

	if (!context) {
		throw new Error('useData must be used within an DataProvider.');
	}

	return context;
}

export { DataContext, useData };
